import axios from "axios";
import { wrapper } from "../utils/wrapper";
import { API_URL } from "../config";
import { keycloak } from "@/main";
import { handleUnauthorizedError } from "@/utils/user";
import { router } from "@/router";
import { sanitize, setDomPurifyIframeValidationHook } from "../utils/sanitize";
import { allowIframesInfo } from "../config";
import DOMPurify from "dompurify";
import { keycloakOptions } from "@/utils/keycloak";


axios.interceptors.request.use(async config => {
  const keycloakToken = await wrapper(keycloak.getKeycloakAccessToken());
  if (keycloakToken.error) {
    console.error(
      "Error axios interceptor getting keycloak token: ",
      keycloakToken.error
    );
    throw new Error("Error axios interceptor getting keycloak token");
  }


  // InAppBrowser excetion to the normal flow
  if (keycloakToken.data === undefined) {
    const token = await wrapper(keycloak.getInAppBrowserToken())
    if (token.error)
      throw new Error("Error axios interceptor getting inAppBrowser token");
    if (token.data) {
      config.headers.Authorization = `Bearer ${token.data}`
      return config
    }
  }
  config.headers.Authorization = `Bearer ${keycloakToken.data}`;
  return config;
});

axios.interceptors.response.use(
  response => {
    let domPurifyConfig = {};

    if (/\/moodbuster-patient-service\//.test(response.config.url!) && response.data) {

      // If is endpoint that may return an iframe in the response body, configure DOMPurify to allow it
      if (response.config.method!.toLowerCase() == allowIframesInfo.endpoint.method && allowIframesInfo.endpoint.url.test(response.config.url!)) {
        setDomPurifyIframeValidationHook(DOMPurify, allowIframesInfo.srcRegexPattern);
        domPurifyConfig = allowIframesInfo.domPurifyconfig;
      }

      sanitize(response.data, DOMPurify, domPurifyConfig);
    }
    return response;
  },
  async error => {
    if (
      error.response &&
      error.response.request &&
      error.response.request.status
    ) {
      if (error.response.request.status == 401) {
        const keycloakAccessTokenExists = await keycloak.getKeycloakAccessToken();

        if (keycloakAccessTokenExists) {
          const renewToken = await wrapper(keycloak.updateToken());

          if (renewToken.error) {
            await handleUnauthorizedError(
              keycloak,
              "LOGIN.ERROR_RENEWING_TOKEN_TITLE",
              "LOGIN.ERROR_RENEWING_TOKEN_DETAIL"
            );
            router.push({ name: "Login" });
          }
        } else {
          // Just in case that the token is not present it will try to perform a login and redirect to the current route
          await keycloak.login(router.currentRoute.value.path);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default class ApiService {
  async getPatient(patientsId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}`)
    );

    if (response.error) {
      throw response.error;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async tos(userId: number) {
    const data = {
      userID: userId
    };
    const response = await wrapper(axios.put(API_URL + "/api/v2/pp/tos", data));
    if (response.error) {
      console.error("axios error tos", response.error);
      throw response.error;
    }
    if (response.data) {
      console.log("response tos ok ", response.data);
      return response.data;
    }
  }

  async privacy(userId: number) {
    const data = {
      userID: userId
    };
    const response = await wrapper(
      axios.put(API_URL + "/api/v2/pp/privacy", data)
    );
    if (response.error) {
      console.error("axios error privacy", response.error);
      throw response.error;
    }
    if (response.data) {
      console.log("response privacy ok ", response.data);
      return response.data;
    }
  }

  async reset(patientsId: number, password: string) {
    const data = {
      password: password
    };
    const response = await wrapper(
      axios.patch(`${API_URL}/patients/${patientsId}`, data)
    );
    if (response.error) {
      console.error("axios error edit Patient", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getEvents(patientsId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}/events`)
    );
    if (response.error) {
      console.error("error get events", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getTimeSeries(patientsId: number, eventId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}/events/${eventId}/timeSeries`)
    );
    if (response.error) {
      console.error("error get event timeSeries", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      // events in the time series are retrieved ordered ascending by "startsAt" datetime
      return response.data.data;
    }
  }

  async createEvent(event: any, patientsId: number) {
    const response = await wrapper(
      axios.post(`${API_URL}/patients/${patientsId}/events`, event)
    );
    if (response.error) {
      console.error("error creating event", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async updateEvent(eventsId: number, event: any, patientsId: number) {
    const response = await wrapper(
      axios.patch(`${API_URL}/patients/${patientsId}/events/${eventsId}`, event)
    );
    if (response.error) {
      console.error("error update event", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data;
    }
  }

  async checkEventAnswer(eventsId: number, patientsId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}/events/${eventsId}/hasAnswer`)
    );
    if (response.error) {
      console.error("error checking for event answer", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data;
    }
  }
  
  async deleteEvent(eventsId: number, patientsId: number) {
    const response = await wrapper(
      axios.delete(`${API_URL}/patients/${patientsId}/events/${eventsId}`)
    );
    if (response.error) {
      console.error("error delete event", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getPageElements(patientsId: number, pagesId: number) {
    const response = await wrapper(
      axios.get(
        `${API_URL}/patients/${patientsId}/pages/${pagesId}?exercisesOnly=true`
      )
    );
    if (response.error) {
      console.error("error get page elements", response.error);
      throw response.error;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getExerciseElement(patientId: string, elementId: string) {
    const response = await wrapper(
      axios.get(
        API_URL + "/api/v2/pp/patient/" + patientId + "/element/" + elementId
      )
    );
    if (response.error) {
      console.error("error get element", response.error);
      throw response.error;
    }
    if (response.data) {
      return response.data;
    }
  }

  async openLoggerPage(page: string, patientsId: number) {
    const response = await wrapper(
      axios.post(`${API_URL}/patients/${patientsId}/patientsLoggerPages`, {
        pageType: page,
        openedAt: new Date().toISOString()
      })
    );
    if (response.error) {
      console.error("error openLoggerPage", response.error);
      throw response.error;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async closeLoggerPage(loggerPageId: number, patientsId: number) {
    const response = await wrapper(
      axios.put(
        `${API_URL}/patients/${patientsId}/patientsLoggerPages/${loggerPageId}`,
        {
          closedAt: new Date().toISOString()
        }
      )
    );
    if (response.error) {
      console.error("error closeLoggerPage", response.error);
      throw response.error;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async createLoggerAction(
    patientsId: number,
    patientLoggerPagesId: number,
    data: any
  ) {
    const response = await wrapper(
      axios.post(
        `${API_URL}/patients/${patientsId}/patientLoggerPages/${patientLoggerPagesId}/patientLoggerActions`,
        data
      )
    );
    if (response.error) {
      console.error("error createLoggerAction", response.error);
      throw response.error;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getMessages(patientsId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}/messages`)
    );
    if (response.error) {
      console.error("error get message", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async createMessage(message: any, patientsId: number) {
    const response = await wrapper(
      axios.post(`${API_URL}/patients/${patientsId}/messages`, message)
    );
    if (response.error) {
      console.error("error post message", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async markMessageAsRead(messagesId: any, data: any, patientsId: number) {
    const response = await wrapper(
      axios.patch(
        `${API_URL}/patients/${patientsId}/messages/${messagesId}`,
        data
      )
    );
    if (response.error) {
      console.error(
        "error markMessageAsRead message",
        response.error.response.data
      );
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getModules(patientsId: number, treatmentsId: number) {
    const response = await wrapper(
      axios.get(
        `${API_URL}/patients/${patientsId}/treatments/${treatmentsId}/modules`
      )
    );
    if (response.error) {
      console.error("error get module", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getModule(patientsId: number, modulesId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}/modules/${modulesId}`)
    );
    if (response.error) {
      console.error("error get module", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getPage(patientsId: number, pagesId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}/pages/${pagesId}`)
    );
    if (response.error) {
      console.error("error get page", response.error.response.data);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async getPageGroups(patientsId: number, pagesId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}/pages/${pagesId}/group`)
    );
    if (response.error) {
      console.error("error get page groups", response.error.response);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async startModule(moduleId: string) {
    const response = await wrapper(
      axios.post(API_URL + "/api/v2/pp/module/" + moduleId)
    );
    if (response.error) {
      console.error("error start module", response.error);
      throw response.error;
    }
    if (response.data) {
      console.log("start module response ", response.data);
      return response.data;
    }
  }

  async endModule(patientsId: string, modulesId: string, progress: any) {
    const moduleData = {
      progress: progress
    };
    const response = await wrapper(
      axios.patch(
        `${API_URL}/patients/${patientsId}/modules/${modulesId}/endModule`,
        moduleData
      )
    );
    if (response.error) {
      console.error("error end module", response.error.response);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async saveAnswer(
    patientsId: string,
    pagesId: string,
    formatForApiArray: any
  ) {
    const response = await wrapper(
      axios.post(
        `${API_URL}/patients/${patientsId}/pages/${pagesId}/answers`,
        formatForApiArray
      )
    );
    if (response.error) {
      console.error("error save page", response.error.response);
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async changePage(patientsId: string, pagesStatesId: string) {
    const pageData = {
      finished: new Date().toISOString()
    };
    const response = await wrapper(
      axios.put(
        `${API_URL}/patients/${patientsId}/pagesStates/${pagesStatesId}`,
        pageData
      )
    );
    if (response.error) {
      console.error("error update page", response.error);
      throw response.error;
    }
    if (response.data) {
      return response.data;
    }
  }

  async changeActivePage(patientsId: string, pagesStatesId: string) {
    const response = await wrapper(
      axios.put(
        `${API_URL}/patients/${patientsId}/pagesStates/${pagesStatesId}/changeActivePage`
      )
    );
    if (response.error) {
      console.error("error update page", response.error);
      throw response.error;
    }
    if (response.data) {
      return response.data;
    }
  }

  async deleteAnswer(
    patientsId: string,
    pagesElementsId: string,
    index: string
  ) {
    const response = await wrapper(
      axios.patch(
        `${API_URL}/patients/${patientsId}/pagesElements/${pagesElementsId}/answers/${index}`
      )
    );
    if (response.error) {
      console.error("error delete answer", response.error);
      throw response.error;
    }
    if (response.data) {
      return response.data;
    }
  }

  async getTreatment(patientsId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientsId}/treatments`)
    );
    if (response.error) {
      console.error(
        "error get treatment modules",
        response.error.response.data
      );
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async updateTreatmentState(data: any) {
    const response = await wrapper(
      axios.put(API_URL + "/api/v2/pp/treatment", data)
    );
    if (response.error) {
      console.error("error update Treatment State", response.error);
      throw response.error;
    }
    if (response.data) {
      console.log("update Treatment State response ", response.data);
      return response.data;
    }
  }

  async getTreatmentState(patientId: number, treatmentId: number) {
    const response = await wrapper(
      axios.get(`${API_URL}/patients/${patientId}/treatment/${treatmentId}/treatmentsStates`)
    );
    if (response.error) {
      console.error(
        "error get treatment state",
        response.error.response.data
      );
      throw response.error.response;
    }
    if (response.data) {
      return response.data.data;
    }
  }

  async logoutOTP() {
    const params = new URLSearchParams();
    params.append("client_id", keycloakOptions.clientId);
    params.append("refresh_token", (await keycloak.get()).refreshToken);

    const response = await wrapper(
      axios.post(
        `${keycloakOptions.url}/realms/${keycloakOptions.realm}/protocol/openid-connect/logout`,
        params
      )
    );
    if (response.error) {
      console.error(
        "error on Logout rendering access token useless",
        response.error
      );
      throw response.error;
    }
    if (response.data) {
      return response.data;
    }
  }
}
